import {ChangeEvent, FC, useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {Box, Checkbox, IconButton, styled, Typography, useMediaQuery} from '@mui/material';
import {Trans, useTranslation} from 'react-i18next';

import {DeleteIconWithBg} from 'assets/icons/deleteIconWIthBg';
import DeleteOutlineIcon2 from 'assets/icons/deleteOutlineIcon2';
import {Express3HourIconEN, Express3HourIconTH} from 'assets/icons/express3HourIcon';
import {MinusIconWithBg} from 'assets/icons/minusIconWIthBg';
import {PlusIconWithBg} from 'assets/icons/plusIconWIthBg';
import {AddToCartButton} from 'components/add-to-cart-button/AddToCartButton';
import {TooltipExtended} from 'components/tooltip-extended/TooltipExtended';
import UpchargeBanner from 'components/upcharge-banner/UpchargeBanner';
import theme from 'config/theme';
import {BASKET_UPDATE_ITEM_SOURCE_EVENTS, BASKET_UPDATE_ITEM_TRIGGER_TYPE} from 'contexts/analytics-context';
import {useAppDispatch, useAppSelector} from 'hooks';
import {useAppliedOmniChannelVoucher} from 'hooks/useAppliedOmniChannelVoucher';
import {useAppMode} from 'hooks/useAppMode';
import {useSelectedItemsLocalStorage} from 'hooks/useSelectedItemsLocalStorage';
import ReactNative from 'services/react-native';
import SendBrowserEvent from 'services/send-browser-event';
import {setAlertAction} from 'store/actions/alert-action';
import {checkedUnselectItemIdsAction, selectItemIdsAction} from 'store/actions/basket-action';
import {AlertType} from 'store/types/app';
import {AnalyticsData, BasketLineItem, Product, ProductType, Variant} from 'types';
import {Voucher} from 'types/campaign';
import {formatPrice, getCurrencySymbol} from 'utils';
import {isUnifiedBasketEnabled, safeToFixed} from 'utils/helper';
import {ProductVoucher} from 'views/basket-product/ProductVoucher';
import DiscountPriceBox from 'views/discount-price-box/DiscountPriceBox';
import {UpchargePopup} from 'views/upcharge-popup/UpchargePopup';
import {WeightedFreshPopup} from 'views/weighted-fresh-popup/WeightedFreshPopup';

import {
  ProductAttributesBox,
  QuantityAdjustedText,
  QuantitySectionStyled,
  StyledAddToCartButtonWrapper,
  StyledCard,
  StyledCardActions,
  StyledCardContent,
  StyledCardContentWrapper,
  AvailableProductsTitle,
  StyledTitleTypographyWrapper,
  StyledTooltipText
} from './styles';
import {WeightedFreshPerUnitPrice} from './WeightedFreshPerUnitPrice';
import {BasketProductImage} from '../../components/basket-product-image/BasketProductImage';
import {DEFAULT_LANGUAGE, zIndexes} from '../../constants';
import ComplexPromotionTag from '../basket-promotions/ComplexPromotionTag';

const voucherQuantityLimit = 1; // HY 250618: it's confirmed for now as the omni voucher channel only have limit of 1 unit and 1 item only
export const QuantityPickerContainer = styled(Box)<{isReactNativeView?: boolean}>(({theme, isReactNativeView}) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  border: `1px solid ${theme.palette.primary.main}`,
  padding: isReactNativeView ? `${theme.spacing(0.87)} 2px` : `${theme.spacing(0.87)} ${theme.spacing(2)}`,
  borderRadius: theme.spacing(4.5),
  overflow: isReactNativeView ? 'none' : 'hidden',
  '& > button:hover': {
    color: theme.palette.common.black
  },
  height: theme.spacing(2.5),
  fontSize: theme.spacing(2.5),
  width: isReactNativeView ? '112px' : 'auto',
  '&.PDPRevamp': {
    border: 'none',
    backgroundColor: theme.palette.grey[50]
  }
}));

const StyledInputField = styled(Box)<{isReactNativeWebView?: boolean}>(({theme, isReactNativeWebView}) => ({
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'column',
  '& input': {
    border: 0,
    outline: 'none',
    maxWidth: theme.spacing(5),
    textAlign: 'center',
    fontWeight: theme.typography.fontWeightBold,
    color: theme.palette.primary.main,
    fontSize: isReactNativeWebView ? theme.spacing(1.5) : theme.spacing(2),
    paddingLeft: theme.spacing(0.5),
    '&.PDPRevamp': {
      backgroundColor: theme.palette.grey[50]
    }
  },
  '& span': {
    marginLeft: theme.spacing(0.5), // Adjust space between input and span
    fontSize: isReactNativeWebView ? '8px' : theme.spacing(1.25),
    fontWeight: 400,
    color: '#6B6B6B'
  }
}));
export const BgIconHolder = styled(Box)<{isReactNativeView?: boolean}>(({theme, isReactNativeView}) => ({
  cursor: 'pointer',
  display: 'flex',
  position: 'relative',
  '&.left': {
    left: isReactNativeView ? theme.spacing(0) : theme.spacing(-1.875)
  },
  '&.right': {
    right: isReactNativeView ? theme.spacing(0) : theme.spacing(-1.875)
  }
}));

export type BasketProductProps = {
  coverImageURL: string;
  title: string;
  index: number;
  weight: string;
  priceUnit: string;
  originPrice: number;
  displayPrice: number;
  quantity: number;
  moq: number;
  onQuantityChange: (qty: number, analyticsData?: AnalyticsData) => Promise<void>;
  productId: string;
  variantId: string;
  basketItem: BasketLineItem;
  loading: boolean;
  maxQuantity: number;
  isProductUnavailable?: boolean;
  productUnavailableText?: string;
  promotedPrice?: number;
  shownQuantityReduceSign?: boolean;
  productType: ProductType;
  upchargePercentage: number;
  upchargeMinimumThreshold: number;
  product?: Product;
  productCappedLimit: number;
  remainingQuantity: number;
  isQuantityAdjusted: boolean;
  isAlcoUnavailableSection?: boolean;
  originPriceLineTotal: number;
  displayPriceLineTotal: number;
  promotedPriceLineTotal: number;
  selectableVouchers?: Voucher[];
  selectedVariant?: Variant;
};

export const BasketProduct: FC<BasketProductProps> = ({
  title,
  coverImageURL,
  onQuantityChange,
  quantity,
  priceUnit,
  originPrice,
  displayPrice,
  weight,
  productId,
  basketItem,
  loading,
  maxQuantity,
  isProductUnavailable,
  productUnavailableText = '',
  promotedPrice,
  productType,
  upchargePercentage,
  upchargeMinimumThreshold,
  moq,
  productCappedLimit,
  variantId,
  remainingQuantity,
  isQuantityAdjusted,
  isAlcoUnavailableSection,
  originPriceLineTotal,
  displayPriceLineTotal,
  promotedPriceLineTotal,
  product,
  selectedVariant
}) => {
  const {t, i18n} = useTranslation();
  const isThai = i18n.language === DEFAULT_LANGUAGE;
  const dispatch = useAppDispatch();
  const {isExpress} = useAppMode();

  const [quantityValue, setQuantityValue] = useState(0);
  const [isQuantityUpdatedToMax, setIsQuantityUpdatedToMax] = useState(false);
  const [quantityLoading, setQuantityLoading] = useState(false);
  const [showUpchargeModal, setShowUpchargeModal] = useState(false);
  const [showWeightedFreshModal, setShowWeightedFreshModal] = useState(false);
  // need 2 state for this handling, because one can be triggered by user hover/click, and the other one
  // is by some system adjustment such as quantity adjustment, where the user didn't hover the button
  const [isAddTooltipHovered, setIsAddTooltipHovered] = useState(false); // used for controlled hover state
  const [isAddTooltipOpen, setIsAddTooltipOpen] = useState(false); // used for action triggered state

  const [isInputFocused, setIsInputFocused] = useState(false);

  const showUpchargeBanner = !basketItem.productDetails.isWeightedFresh && basketItem.productDetails.isUpchargeApplied;

  const {variantIdsOfItemThatWasAdjustedQuantity, basket} = useAppSelector((state) => state?.basket);
  const showWeightedFreshBanner =
    basketItem.productDetails.isWeightedFresh && upchargePercentage > 0 && quantity >= upchargeMinimumThreshold;

  const shownQuantityReduceSign = variantIdsOfItemThatWasAdjustedQuantity.includes(variantId);
  const isMobileView = useMediaQuery(theme.breakpoints.only('mobile'));
  const selectedItemsLocalStorage = useSelectedItemsLocalStorage();
  const selectedItems = selectedItemsLocalStorage.get();

  const isReactNativeView = ReactNative.isReactNativeWebView();
  const isOmniVoucherPlatformEnabled = isReactNativeView;
  const currencySymbol = getCurrencySymbol();
  const appliedOmniChannelVoucher = useAppliedOmniChannelVoucher();
  const isMobileScreen = isReactNativeView || isMobileView;

  const isUnifiedBasketEnabledFlag = isUnifiedBasketEnabled();

  const timer = useRef<any>(undefined);
  const analyticsData = {
    sourceEvent: '',
    triggerType: '',
    productId: basketItem?.productId || '',
    productPrice: basketItem?.productDetails?.displayPrice || '',
    qty: 0
  };

  const isWeightedFresh = basketItem?.productDetails?.isWeightedFresh || false;
  const weightedFreshPerUnitPrice = basketItem?.productDetails?.weightedFreshPricePerUnit;
  const showKg = isWeightedFresh;

  const currentItemOmniVoucher = useMemo(() => {
    if (!isOmniVoucherPlatformEnabled) {
      return;
    }
    return appliedOmniChannelVoucher?.skus.includes(basketItem.productDetails.sku);
  }, [isOmniVoucherPlatformEnabled, appliedOmniChannelVoucher?.skus, basketItem.productDetails.sku]);

  // current mechanic as of june 2025
  // only 1 omni voucher will be able to be aplied
  // each omni voucher is guaranteed to only contain 1 sku
  // when there is no omni voucher applied, just show the possible voucher for every item -> !!currentItemOmniVoucher
  // when there is omni voucher applied, only show the voucher that is applied, the rest are hidden in each product -> true
  //
  const shouldThisItemShowVoucher = isOmniVoucherPlatformEnabled && !appliedOmniChannelVoucher;
  const quantityLimit = showKg ? quantity * moq : quantity;
  const minimumQuantity = showKg ? moq : 1;
  const selectedQuantity = showKg ? quantityValue * moq : quantityValue;
  const DecrementIconWithBg = selectedQuantity > minimumQuantity ? MinusIconWithBg : DeleteIconWithBg;

  const displayPriceForDiscountPriceBox = promotedPrice ? promotedPrice : displayPrice;
  const displayPriceLineTotalForDiscountPriceBox = promotedPriceLineTotal
    ? promotedPriceLineTotal
    : displayPriceLineTotal;
  const isQuantityMultipleOfMoq = Number.isInteger(quantityValue / moq);
  const debounceFunction = (main: () => void) => {
    return setTimeout(main, 1500);
  };

  const {weight: measuredWeight, weightUnit} = basketItem.productDetails?.measurements || {};
  const productEstimatedPcs = basketItem?.productDetails?.quantitySetByUser;

  const productUnitPropsDescription =
    measuredWeight && weightUnit && isExpress
      ? `${weight} | ${formatPrice(basketItem.productDetails.displayPrice)} ${getCurrencySymbol()} | ${basketItem.productDetails?.measurements?.weight} ${basketItem.productDetails?.measurements?.weightUnit}. / ${t('unit')}`
      : `${weight} | ${displayPriceForDiscountPriceBox && `${displayPriceForDiscountPriceBox} ${currencySymbol}`}`;

  const isCappedLimitReached = remainingQuantity === 0;

  const isOmniVoucherQuantityLimited = currentItemOmniVoucher && quantity >= voucherQuantityLimit;
  const disableAddIcon =
    quantityLoading || isQuantityUpdatedToMax || isCappedLimitReached || isOmniVoucherQuantityLimited;
  const disabledAddButtonTooltip = useMemo(() => {
    if (isOmniVoucherQuantityLimited) {
      return {
        key: 'message.voucherQuantityLimitedMessage',
        values: {quantity: voucherQuantityLimit}
      };
    }

    if (
      (isQuantityAdjusted && !!productCappedLimit) ||
      (!!productCappedLimit && !!selectedVariant?.inventoryQuantity && selectedVariant?.inventoryQuantity > quantity)
    ) {
      return {
        key: 'message.orderCappingMessage',
        values: {quantity: quantityLimit}
      };
    }

    if (!isQuantityMultipleOfMoq && !isQuantityUpdatedToMax) {
      return {
        key: 'message.quantityAdjustedMessage',
        values: {quantity: moq}
      };
    }

    return {
      key: 'message.maxQuantityReached',
      values: {quantity: moq}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isOmniVoucherQuantityLimited,
    isQuantityAdjusted,
    productCappedLimit,
    quantity,
    selectedVariant?.inventoryQuantity,
    quantityLimit,
    isQuantityMultipleOfMoq,
    isQuantityUpdatedToMax,
    moq
  ]);
  useEffect(() => {
    if (quantity) {
      setQuantityValue(quantity);
      setIsQuantityUpdatedToMax(quantity === maxQuantity);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quantity, isQuantityAdjusted]);

  const showAddTooltip = useCallback(() => {
    setIsAddTooltipOpen(true);
    setTimeout(() => {
      setIsAddTooltipOpen(false);
    }, 3000);
  }, []);

  useEffect(() => {
    if (isInputFocused && showKg) {
      setQuantityValue(quantity * moq);
    } else {
      setQuantityValue(quantity);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInputFocused]);

  useEffect(() => {
    if (remainingQuantity === 0 && isQuantityAdjusted) {
      showAddTooltip();
    }
  }, [isQuantityAdjusted, remainingQuantity, showAddTooltip]);

  const handleQuantityChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = Number(e.currentTarget.value);
    if (!isNaN(value)) {
      if (value > maxQuantity) {
        setQuantityValue(maxQuantity);
        setIsQuantityUpdatedToMax(true);
        showAddTooltip();
      } else {
        setQuantityValue(value);
        setIsQuantityUpdatedToMax(value === maxQuantity);
      }
    }
  };

  const onSingleItemAdjustment = async (quantity: number) => {
    if (quantity < 1 || quantity > maxQuantity) {
      return;
    }
    try {
      setQuantityLoading(true);
      setIsQuantityUpdatedToMax(quantity === maxQuantity);
      showAddTooltip();
      let sourceEvent = null;
      if (quantity > basketItem?.quantity) {
        sourceEvent = BASKET_UPDATE_ITEM_SOURCE_EVENTS.INCREASE_ITEM;
      } else if (quantity < basketItem?.quantity) {
        sourceEvent = BASKET_UPDATE_ITEM_SOURCE_EVENTS.DECREASE_ITEM;
      }
      if (sourceEvent) {
        analyticsData.qty = quantity;
        analyticsData.triggerType = BASKET_UPDATE_ITEM_TRIGGER_TYPE.BUTTON;
        analyticsData.sourceEvent = sourceEvent;
        await onQuantityChange(quantity, analyticsData);
      } else {
        await onQuantityChange(quantity);
      }
    } catch {
      // ignored
      setQuantityValue(basketItem.quantity);
    } finally {
      setQuantityLoading(false);
    }
  };

  const onFocus = () => {
    showKg && setIsInputFocused(true);
  };

  const onQuantityInputBlur = async () => {
    try {
      setQuantityLoading(true);
      let sourceEvent = null;
      if (quantityValue > basketItem?.quantity) {
        sourceEvent = BASKET_UPDATE_ITEM_SOURCE_EVENTS.INCREASE_ITEM;
      } else if (quantityValue < basketItem?.quantity) {
        if (quantityValue === 0) {
          sourceEvent = BASKET_UPDATE_ITEM_SOURCE_EVENTS.DELETE_ITEM;
        } else {
          sourceEvent = BASKET_UPDATE_ITEM_SOURCE_EVENTS.DECREASE_ITEM;
        }
      }
      if (sourceEvent) {
        analyticsData.qty = quantityValue;
        analyticsData.triggerType = BASKET_UPDATE_ITEM_TRIGGER_TYPE.KEYBOARD;
        analyticsData.sourceEvent = sourceEvent;
        if (showKg) {
          await onQuantityChange(Math.round(quantityValue / moq), analyticsData);
          setQuantityValue(quantityValue);
          showAddTooltip();
        } else {
          await onQuantityChange(quantityValue, analyticsData);
        }
      } else {
        await onQuantityChange(quantity);
      }
      setIsInputFocused(false);
      setIsQuantityUpdatedToMax(quantityValue === maxQuantity);
    } catch {
      setIsInputFocused(false);
      setQuantityValue(quantity);
    } finally {
      setQuantityLoading(false);
    }
  };

  const formattedValueForNewInputUI = useMemo(() => {
    if (showKg) {
      return `${Number.isInteger(moq) ? quantityValue * moq : safeToFixed(quantityValue * moq)}`;
    } else {
      return Number(quantityValue);
    }
  }, [showKg, quantityValue, moq]);

  const handleDelete = async () => {
    analyticsData.sourceEvent = BASKET_UPDATE_ITEM_SOURCE_EVENTS.DELETE_ITEM;
    analyticsData.triggerType = BASKET_UPDATE_ITEM_TRIGGER_TYPE.BUTTON;
    analyticsData.qty = 0;
    await onQuantityChange(0, analyticsData);
    SendBrowserEvent.showInfoToast(t('message.productRemoved'));
    // await deleteLineItem(variantId, basketItem, analyticsData, undefined);
  };

  const checkPromptDelete = () => {
    const appliedVoucherPromotion = basket?.appliedVoucherPromotion?.[0];
    if (!appliedVoucherPromotion) {
      handleDelete();
      return;
    }
    if (appliedVoucherPromotion.skus[0] !== basketItem.productDetails.sku) {
      handleDelete();
      return;
    }
    dispatch(
      setAlertAction({
        type: AlertType.voucherItemRemoved,
        details: {
          callback: handleDelete
        }
      })
    );
  };

  const updateQuantity = (action: 'increment' | 'decrement') => {
    const latestQuantity = action == 'increment' ? quantityValue + 1 : quantityValue - 1;

    const isWithinMaxQuantityLimit = action == 'increment' ? latestQuantity <= maxQuantity : Boolean(latestQuantity);
    if (isWithinMaxQuantityLimit) {
      clearTimeout(timer.current);
      setQuantityValue(latestQuantity);
      timer.current = debounceFunction(() => onSingleItemAdjustment(latestQuantity));
    }
  };

  const goToPdp = () => {
    SendBrowserEvent.goToPDP(productId);
  };

  const toggleCheck = (e: ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    if (checked) {
      dispatch(selectItemIdsAction([productId]));
    } else {
      dispatch(checkedUnselectItemIdsAction([productId]));
    }
  };

  return (
    <StyledCard isReactNativeView={isMobileScreen} data-test-product-id={productId} data-test-product-name={title}>
      <Box display="flex" gap={isMobileScreen ? '8px' : '0px'}>
        {!isProductUnavailable && (
          <Box width="5%" minWidth={'25px'} paddingRight={isMobileScreen ? '0px' : '16px'}>
            <Checkbox
              name="isDefault"
              onChange={toggleCheck}
              defaultChecked={isAlcoUnavailableSection ? false : selectedItems?.includes(productId)}
              checked={isAlcoUnavailableSection ? false : selectedItems?.includes(productId)}
              disabled={loading || isAlcoUnavailableSection}
              data-test-id={`${productId}_product_card_checkbox`}
              sx={{
                '&.Mui-disabled': {color: theme.palette.primary.light},
                color: '#C7C7C7',
                '&.Mui-checked': {
                  color: 'primary'
                },
                height: 'fit-content',
                padding: 0
              }}
            />
          </Box>
        )}
        <Box width="95%">
          <Box display="flex" gap={isMobileScreen ? '8px' : '0px'}>
            <Box
              onClick={() => {
                SendBrowserEvent.goToPDP(productId);
              }}
            >
              <BasketProductImage
                src={coverImageURL}
                productType={productType}
                isBulky={basketItem.productDetails.isBulky}
              />
            </Box>

            <StyledCardContentWrapper role="button" tabIndex={0} onKeyDown={() => null}>
              <Box display="flex" alignItems={'flex-start'} justifyContent="space-between">
                <Box>
                  <StyledCardContent isReactNativeView={isMobileScreen}>
                    <StyledTitleTypographyWrapper onClick={goToPdp}>
                      <AvailableProductsTitle
                        color="secondary"
                        gutterBottom
                        variant="body1"
                        component="p"
                        isReactNativeView={isMobileScreen}
                      >
                        <b>{title}</b>
                      </AvailableProductsTitle>
                    </StyledTitleTypographyWrapper>
                    <ProductAttributesBox onClick={goToPdp}>
                      {isUnifiedBasketEnabledFlag &&
                        product?.isExpressEligible &&
                        !isExpress &&
                        (isThai ? <Express3HourIconTH /> : <Express3HourIconEN />)}
                      {isWeightedFresh && weightedFreshPerUnitPrice ? (
                        <WeightedFreshPerUnitPrice
                          perUnitPrice={weightedFreshPerUnitPrice}
                          size={weight}
                          isWeightedFreshV3Enabled={isWeightedFresh}
                        ></WeightedFreshPerUnitPrice>
                      ) : (
                        <Typography display={'block'} variant="caption" color="text.secondary">
                          {productUnitPropsDescription}
                        </Typography>
                      )}
                    </ProductAttributesBox>

                    {shouldThisItemShowVoucher && <ProductVoucher basketItem={basketItem} />}
                  </StyledCardContent>
                  <ComplexPromotionTag basketItem={basketItem} />
                </Box>
                {!isMobileScreen && (
                  <IconButton
                    data-test-delete-btn=""
                    disabled={loading}
                    type="button"
                    onClick={checkPromptDelete}
                    size="small"
                  >
                    <DeleteOutlineIcon2 fillColor="#8E8E8E" fontSize="small" />
                  </IconButton>
                )}
              </Box>
            </StyledCardContentWrapper>
          </Box>

          <StyledCardActions isReactNativeView={isMobileScreen}>
            <Box>
              <DiscountPriceBox
                originPrice={originPriceLineTotal}
                priceUnit={priceUnit}
                displayPrice={displayPriceLineTotalForDiscountPriceBox}
                isVoucherUsed={!!currentItemOmniVoucher}
              />
              {showKg && (
                <Typography variant="caption" color="#212124" fontSize={12} fontWeight={400}>
                  {productEstimatedPcs === 1
                    ? t('weightedFresh.estimatedPc', {estimatedPcs: productEstimatedPcs})
                    : t('weightedFresh.estimatedPcs', {estimatedPcs: productEstimatedPcs})}
                </Typography>
              )}
            </Box>
            {isProductUnavailable ? (
              <StyledAddToCartButtonWrapper>
                <AddToCartButton
                  disableRipple={true}
                  loading={loading}
                  disabled={true}
                  title={productUnavailableText}
                  // eslint-disable-next-line @typescript-eslint/no-empty-function
                  onAdd={() => {}}
                />
              </StyledAddToCartButtonWrapper>
            ) : (
              <QuantitySectionStyled isReactNativeWebView={isMobileScreen}>
                {shownQuantityReduceSign && (
                  <QuantityAdjustedText isReactNativeWebView={isMobileScreen}>
                    {t('message.quantityAdjusted')}
                  </QuantityAdjustedText>
                )}
                <QuantityPickerContainer className={'PDPRevamp'} isReactNativeView={isMobileScreen}>
                  <BgIconHolder
                    className="left"
                    data-test-id={quantityValue > 1 ? `${productId}_decrement_quantity` : `${productId}_remove_quanity`}
                    onClick={() => {
                      if (!quantityLoading)
                        selectedQuantity > minimumQuantity ? updateQuantity('decrement') : checkPromptDelete();
                    }}
                    isReactNativeView={isMobileScreen}
                  >
                    <DecrementIconWithBg disabled={quantityLoading} />
                  </BgIconHolder>
                  <StyledInputField isReactNativeWebView={isMobileScreen}>
                    <input
                      disabled={quantityLoading}
                      onChange={handleQuantityChange}
                      onBlur={() => {
                        clearTimeout(timer.current);
                        timer.current = debounceFunction(() => onQuantityInputBlur());
                      }}
                      data-test-id={`${productId}_product_quantity_input`}
                      onFocus={onFocus}
                      value={isInputFocused && showKg ? quantityValue : formattedValueForNewInputUI}
                      name={'quantity'}
                      autoComplete="off"
                      inputMode={showKg ? 'decimal' : 'numeric'}
                      type={'text'}
                      className={'PDPRevamp'}
                    />
                    {showKg && !isInputFocused && <span>{t('labels.Kg')}</span>}
                  </StyledInputField>

                  <TooltipExtended
                    disabled={!disableAddIcon}
                    title={
                      <StyledTooltipText>
                        <Trans
                          t={t}
                          components={{1: <br />}}
                          i18nKey={disabledAddButtonTooltip.key}
                          values={disabledAddButtonTooltip.values}
                        />
                      </StyledTooltipText>
                    }
                    arrow
                    placement="top-end"
                    PopperProps={{style: {zIndex: zIndexes.maxQuantityReached}}}
                    open={isAddTooltipHovered || isAddTooltipOpen}
                    onMouseLeave={() => setIsAddTooltipHovered(false)}
                    onMouseEnter={() => setIsAddTooltipHovered(true)}
                  >
                    <BgIconHolder
                      className="right"
                      onClick={() => {
                        !disableAddIcon && updateQuantity('increment');
                      }}
                      color={'primary'}
                      data-test-id={`${productId}_increment_quantity`}
                      isReactNativeView={isMobileScreen}
                    >
                      <PlusIconWithBg disabled={disableAddIcon} />
                    </BgIconHolder>
                  </TooltipExtended>
                </QuantityPickerContainer>
              </QuantitySectionStyled>
            )}
          </StyledCardActions>
        </Box>
      </Box>

      {showUpchargeBanner && (
        <UpchargeBanner
          setShowUpchargeModal={setShowUpchargeModal}
          upchargePercentage={upchargePercentage ?? 0}
          isWeightedFresh={false}
        />
      )}

      {showWeightedFreshBanner && (
        <UpchargeBanner
          setShowUpchargeModal={setShowWeightedFreshModal}
          upchargePercentage={upchargePercentage ?? 0}
          isWeightedFresh={true}
        />
      )}

      <UpchargePopup
        showUpchargeModal={showUpchargeModal}
        setShowUpchargeModal={setShowUpchargeModal}
        coverImageURL={coverImageURL}
        title={title}
        productType={productType}
        weight={weight}
        priceUnit={priceUnit}
        originPrice={originPrice}
        displayPrice={displayPrice}
        promotedPrice={promotedPrice}
        upchargePercentage={upchargePercentage}
        upchargeMinimumThreshold={upchargeMinimumThreshold}
      />

      <WeightedFreshPopup
        showWeightedFreshModal={showWeightedFreshModal}
        setShowWeightedFreshModal={setShowWeightedFreshModal}
        coverImageURL={coverImageURL}
        title={title}
        productType={productType}
        weight={weight}
        priceUnit={priceUnit}
        originPrice={originPrice}
        displayPrice={displayPrice}
        promotedPrice={promotedPrice}
        upchargePercentage={upchargePercentage}
      />
    </StyledCard>
  );
};

BasketProduct.displayName = 'Product';
export default BasketProduct;
